// 화면 그리기(§9.8.4) — 단축키. 그리기가 입력을 가져간 동안(`capturesInput`) 세션 화면이 키를 여기로 넘긴다.
// 🔴 여기로 온 키는 **원격으로 보내지 않는다**(무대 격리 — Ctrl+Z 가 원격 앱의 되돌리기까지 부르면 안 된다).
import type { DrawEditor } from './editor.ts';
import type { Space } from './geometry.ts';
import { TOOLS } from './style.ts';

/** 키 이벤트에서 쓰는 값만(DOM 타입에 묶이지 않게 — 시험 대상). */
export interface KeyLike {
  key: string;
  /** 물리 키 위치(`KeyP` …) — 한글 입력 모드에서는 `key` 가 `Process`·`ㅔ` 라서 이것으로 글자 키를 읽는다. */
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/** 도구 단축키 → 도구(데이터: style.ts 의 `TOOLS[].key`). */
const TOOL_BY_KEY = new Map(TOOLS.filter((t) => t.key).map((t) => [t.key!, t.tool]));

/**
 * 단축키 판정용 키 이름(소문자). 🔴 한글 입력 모드에서는 P 를 눌러도 `key` 가 `Process`(Windows) 또는 `ㅔ` 로 온다 —
 * `key` 만 보면 한글 모드에서 단축키가 전부 죽는다. 글자 키는 **물리 위치(`code`)** 로 읽는다.
 */
export function keyName(e: KeyLike): string {
  const m = /^Key([A-Z])$/.exec(e.code ?? '');
  if (m) return m[1].toLowerCase();
  return e.key.toLowerCase();
}

/**
 * 누름만 처리한다(뗌은 무시 — 어느 쪽이든 원격으로는 보내지 않는다). 처리했으면 true.
 * @param space 복제 위치 계산용 캔버스 크기(없으면 복제를 건너뛴다)
 */
export function handleDrawKey(e: KeyLike, ed: DrawEditor, space: Space | null): boolean {
  const key = keyName(e);
  const mod = e.ctrlKey || e.metaKey; // Mac 은 ⌘
  if (mod && !e.altKey) {
    if (key === 'z' && !e.shiftKey) {
      ed.undo();
      return true;
    }
    if ((key === 'z' && e.shiftKey) || key === 'y') {
      ed.redo();
      return true;
    }
    if (key === 'd' && space) {
      ed.duplicateSelection(space);
      return true;
    }
    return false;
  }
  if (e.altKey) return false;
  if (key === 'delete' || key === 'backspace') {
    ed.deleteSelection();
    return true;
  }
  const tool = TOOL_BY_KEY.get(key);
  if (tool) {
    ed.setTool(tool);
    return true;
  }
  return false;
}
